import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { AlertNotice, SimpleModal, ModalInfo } from 'shared/components/index';
import * as roomActions from 'features/room-builder/duck';
import * as seatingChartActions from 'features/seating-chart/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import SeatingDetails from './SeatingDetails';
import './SeatingDetails.scss';

const ROOM_SETUP_REDESIGN_FLAG_CODE = 'room_setup_redesign_enabled';

const seatingDetails = {
  seatingRowCount: '',
  seatingColumnCount: '',
  writingSurfaceWidth: '',
  writingSurfaceDepth: '',
  distanceBetweenShoulders: '',
  distanceBetweenHeads: '',
};

const checkRegSummaries = (regSummaries, updatedRoom, room) => {
  const currentRowCount = +room.seatingRowCount;
  const updatedRowCount = +updatedRoom.seatingRowCount;
  const currentColumnCount = +room.seatingColumnCount;
  const updatedColumnCount = +updatedRoom.seatingColumnCount;

  if (updatedRowCount < currentRowCount) {
    for (let i = updatedRowCount; i < currentRowCount; i++) {
      if (regSummaries[i] && regSummaries[i].length) {
        return true;
      }
    }
    return false;
  }

  if (updatedColumnCount < currentColumnCount) {
    for (let i = 0; i < currentRowCount; i++) {
      if (regSummaries[i] && regSummaries[i].length) {
        for (let j = updatedColumnCount; j < currentColumnCount; j++) {
          if (regSummaries[i][j]) {
            return true;
          }
        }
      }
    }
    return false;
  }

  return false;
};

// Named export (in addition to the default connected/withTranslation-wrapped component)
// so this class can be unit tested directly, mirroring the
// EventRoomsContainer.isRoomSetupRedesignEnabled() pattern's own test convention, without
// needing a real redux store + i18n Provider tree.
export class ModalSeatingDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      room: {
        ...seatingDetails,
        ...props.room,
      },
      warningSeatsModalOpen: false,
      discardModalOpen: false,
      roomUpdated: {},
      errors: {},
      // STATEDIST-11666 follow-up: fails safe to disabled — the child <SeatingDetails>
      // reports its real isDisabled() via onValidityChange on its own mount (before this
      // component's next paint), so this default is only ever visible for a single commit.
      saveDisabled: true,
    };

    this.child = React.createRef();
    this.onSubmit = this.onSubmit.bind(this);
    this.closeWarningModal = this.closeWarningModal.bind(this);
    this.closeSeatingModal = this.closeSeatingModal.bind(this);
    this.handleValidityChange = this.handleValidityChange.bind(this);
  }

  componentDidMount() {
    const { actions } = this.props;

    // Flag-consumption pattern per STATEDIST-11343 (see
    // EventRoomsContainer.isRoomSetupRedesignEnabled()): StaticService.getConfigValues() ->
    // staticDuck.js (GET_CONFIG_VALUES) -> state.statics.configValues -> looked up by code.
    actions.getConfigValues();

    this.maybeFetchRoomOptions();
  }

  // Mirrors EventRoomsContainer.isRoomSetupRedesignEnabled() (STATEDIST-11343 pattern) —
  // duplicated here rather than shared because ModalSeatingDetails lives in a different
  // feature (seating-chart, not room-builder) with its own connected props.
  isRoomSetupRedesignEnabled() {
    const entry = (this.props.configValues || []).find(
      (configValue) => configValue.code === ROOM_SETUP_REDESIGN_FLAG_CODE
    );
    return entry?.value === 'true';
  }

  // Reuses the same flag-filtered GET /seatings call STATEDIST-11172's RoomStructure screen
  // already triggers (roomActions.getRoomOptions) rather than a new endpoint — when the
  // flag is on, this returns exactly the 2 rows the Seating Arrangement selector renders.
  //
  // Bug found manually verifying STATEDIST-11666, reproducible on a hard reload landing
  // directly on the Seating Chart route: this component is a child of
  // SeatingChartDashboard, which fetches tcEvent itself (only if empty) in its OWN
  // componentDidMount — since children mount before their parent's componentDidMount runs,
  // that fetch fires AFTER this component has already mounted. Calling this only from
  // componentDidMount meant the !isEmpty(tcEvent) guard was false on that very first mount
  // and nothing ever retried once tcEvent actually arrived — roomOptions.seating (and
  // therefore the whole tile group, not just the selection) stayed empty for that entire
  // page load. Also called from componentDidUpdate below once tcEvent transitions from
  // empty to populated.
  maybeFetchRoomOptions() {
    const { actions, loadedOptions, tcEvent } = this.props;
    if (!loadedOptions && !isEmpty(tcEvent) && tcEvent.testingEvent) {
      actions.getRoomOptions(tcEvent.testingEvent.adminId.cycleId.id);
    }
  }

  // STATEDIST-11666 follow-up: <SeatingDetails> calls this (via onValidityChange) on its
  // own mount and every update — guard against a redundant setState when the value hasn't
  // actually changed, since that call happens on EVERY child update regardless of cause and
  // an unconditional setState here would otherwise re-render this component, re-render the
  // child, retrigger its componentDidUpdate, and call this again indefinitely.
  handleValidityChange(saveDisabled) {
    if (saveDisabled !== this.state.saveDisabled) {
      this.setState({ saveDisabled });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      !this.state.room.id &&
      !this.props.room.loading &&
      !isEmpty(this.props.room) &&
      prevProps.room !== this.props.room
    ) {
      this.setState({
        room: this.props.room,
      });
    }

    if (isEmpty(prevProps.tcEvent) && !isEmpty(this.props.tcEvent)) {
      this.maybeFetchRoomOptions();
    }
  }

  async onSubmit() {
    const { actions, regSummaries, room } = this.props;

    if (this.child.current && this.child.current.onSubmit) {
      const roomContent = this.child.current.onSubmit();

      if (roomContent && !isEmpty(roomContent)) {
        if (
          regSummaries &&
          regSummaries.length &&
          checkRegSummaries(regSummaries, roomContent, room)
        ) {
          this.setState({
            warningSeatsModalOpen: true,
            roomUpdated: roomContent,
          });
          return;
        }
        const updatedRoom = await actions.updateRoom(roomContent);
        if (updatedRoom && updatedRoom.id) {
          sessionStorage.setItem(
            'successSavedMessage',
            this.props.t('seatingChart.seatingDetails.successMessage')
          );
          actions.getRegSummaries(updatedRoom.id);
          this.closeSeatingModal();
        }
      }
      return;
    }
  }

  async closeWarningModal(submitData) {
    const { roomUpdated } = this.state;

    if (submitData) {
      const updatedRoom = await this.props.actions.updateRoom(roomUpdated);
      if (updatedRoom && updatedRoom.id) {
        this.props.actions.getRegSummaries(updatedRoom.id);
        this.closeSeatingModal(true);
      }
    }
    this.setState({
      warningSeatsModalOpen: false,
      roomUpdated: {},
    });
  }

  closeSeatingModal() {
    this.props.actions.resetErrorRoomAction();

    if (this.child.current && this.child.current.state.isDirty) {
      this.setState({
        discardModalOpen: true,
      });
      return;
    }
    this.props.closeModal();
  }

  closeDiscardModal(discardChangesFlag) {
    this.setState({
      discardModalOpen: false,
    });

    if (discardChangesFlag) {
      this.props.closeModal();
    }
  }

  render() {
    const { room, roomStatus, modalOpen, errorRoom, tcEvent, roomOptions } = this.props;
    const { warningSeatsModalOpen, discardModalOpen, saveDisabled } = this.state;
    const redesignEnabled = this.isRoomSetupRedesignEnabled();

    return (
      <div>
        <ModalInfo
          modalIsOpen={modalOpen}
          closeModal={() => this.closeSeatingModal()}
          headerTitle="seatingChart.seatingDetails.modalTitle"
          onSubmit={this.onSubmit}
          saveLabel="common.labels.save"
          // STATEDIST-11666 follow-up: seat count is required (unconditionally, when
          // redesignEnabled) but Save previously just silently did nothing when it was
          // blank — same "looks clickable but does nothing" gap STATEDIST-11673 fixed for
          // RoomStructure.jsx's Continue button. Honest disabled state instead.
          disabledSubmit={saveDisabled}
          className="modal-seating-details">
          <div>
            <h4>{room.name}</h4>
            <div>{!isEmpty(tcEvent) && tcEvent.testingEvent.name}</div>
            <p>
              <span>{roomStatus.locationName}</span>
              {roomStatus.locationArea && roomStatus.locationArea.length > 0 && (
                <span>, {roomStatus.locationArea}</span>
              )}{' '}
              - <span>{roomStatus.testCenterName}</span>
            </p>
            <SeatingDetails
              ref={this.child}
              room={room}
              required
              redesignEnabled={redesignEnabled}
              seatingOptions={roomOptions.seating || []}
              onValidityChange={this.handleValidityChange}
            />
            {!isEmpty(errorRoom) && (
              <div className="mt-3">
                <AlertNotice message={this.props.t('errors.somethingWentWrong')} type="error" />
              </div>
            )}
          </div>
        </ModalInfo>
        <SimpleModal
          isOpen={warningSeatsModalOpen}
          onCancel={() => this.closeWarningModal(false)}
          onConfirm={() => this.closeWarningModal(true)}
          confirmLabel={this.props.t('common.labels.save')}
          cancelLabel={this.props.t('common.labels.back')}
          headline={this.props.t('seatingChart.removeSeatsModalTitle')}
        />
        <SimpleModal
          isOpen={discardModalOpen}
          onCancel={() => this.closeDiscardModal(false)}
          onConfirm={() => this.closeDiscardModal(true)}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('seatingChart.discardModal.title')}
          message={this.props.t('seatingChart.discardModal.message')}
          buttonsReverseOrder
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    room: state.roomOptions.room,
    errorRoom: state.roomOptions.errorRoom,
    roomOptions: state.roomOptions.options || {},
    loadedOptions: state.roomOptions.loadedOptions,
    configValues: state.statics.configValues,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...roomActions,
        ...seatingChartActions,
        ...staticActions,
      },
      dispatch
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation(undefined, { withRef: true })(ModalSeatingDetails));

ModalSeatingDetails.propTypes = {
  room: PropTypes.shape({}).isRequired,
  regSummaries: PropTypes.arrayOf(PropTypes.array),
  roomOptions: PropTypes.shape({
    seating: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  loadedOptions: PropTypes.bool,
  configValues: PropTypes.arrayOf(PropTypes.shape({})),
  tcEvent: PropTypes.shape({
    testingEvent: PropTypes.shape({
      adminId: PropTypes.shape({
        cycleId: PropTypes.shape({
          id: PropTypes.number,
        }),
      }),
    }),
  }),
  t: PropTypes.func.isRequired,
};

ModalSeatingDetails.defaultProps = {
  roomOptions: {},
  loadedOptions: false,
  configValues: [],
  tcEvent: {},
};
